import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { TableAccess } from '@/api/types'

/**
 * Who may open a table.
 *
 * Admin-only in both directions, which is why nothing subscribes to this
 * speculatively: a member who is not an admin would get a 403 for asking, and
 * the control that opens it is not offered to them.
 */
export function tableAccessQuery(tableId: number) {
  return queryOptions({
    queryKey: qk.tableAccess(tableId),
    queryFn: (): Promise<TableAccess> =>
      unwrap(
        getApi().GET('/api/database/tables/{table_id}/access/', {
          params: { path: { table_id: tableId } },
        }),
      ),
  })
}

export function useTableAccess(tableId: number) {
  return useQuery(tableAccessQuery(tableId))
}

/**
 * Replaces the whole access configuration.
 *
 * The endpoint is a `PUT` and the list is described as replacing any existing
 * one, which is the right shape: "these people" is a statement about all of
 * them, and adding one at a time would let two tabs remove somebody by not
 * mentioning them.
 */
export function setTableAccessMutation(queryClient: QueryClient, tableId: number) {
  return mutationOptions({
    mutationFn: (access: TableAccess): Promise<TableAccess> =>
      unwrap(
        getApi().PUT('/api/database/tables/{table_id}/access/', {
          params: { path: { table_id: tableId } },
          body: access,
        }),
      ),
    onSuccess: async (access) => {
      queryClient.setQueryData(qk.tableAccess(tableId), access)
      // Which tables this account may open is part of what the sidebar draws,
      // and restricting a table can take it away from the person doing it.
      await queryClient.invalidateQueries({ queryKey: qk.workspaces() })
    },
  })
}

export function useSetTableAccess(tableId: number) {
  return useMutation(setTableAccessMutation(useQueryClient(), tableId))
}
